import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  X,
  Settings,
  Zap,
  Loader2,
  CheckCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Play,
  Save,
} from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import {
  runOptimization,
  applyOptimizationSuggestions,
  clearOptimizationResult,
} from '@/store/slices/scenariosSlice';
import { GlassCard, GlassButton, GlassBadge } from '@/design-system';
import type {
  OptimizationConstraints,
  EpicPriority,
  OptimizationResult,
  AllocationSuggestion,
} from '@/types';

interface Epic {
  id: string;
  name: string;
  requestedWeeks?: number;
  investibleStructure?: {
    name: string;
    category: string;
  };
}

interface OptimizationPanelProps {
  scenarioId: string;
  scenarioName: string;
  epics: Epic[];
  isOpen: boolean;
  onClose: () => void;
  onOptimizationApplied?: () => void;
}

const defaultConstraints: OptimizationConstraints = {
  maxTeamUtilization: 0.85,
  minBusinessAllocation: 0.75,
  minTechAllocation: 0.15,
  minSupportAllocation: 0.10,
};

const OptimizationPanel: React.FC<OptimizationPanelProps> = ({
  scenarioId,
  scenarioName,
  epics,
  isOpen,
  onClose,
  onOptimizationApplied,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    optimizationResult,
    optimizationLoading,
    optimizationError,
    applySuggestionsLoading,
    applySuggestionsError,
  } = useSelector((state: RootState) => state.scenarios);

  // State
  const [constraints, setConstraints] = useState<OptimizationConstraints>(defaultConstraints);
  const [priorities, setPriorities] = useState<EpicPriority[]>([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<number>>(new Set());
  const [expandedSections, setExpandedSections] = useState({
    constraints: true,
    priorities: true,
    results: true,
  });

  // Initialize priorities from epics
  useEffect(() => {
    if (epics.length > 0 && priorities.length === 0) {
      const initialPriorities = epics.map((epic, index) => ({
        epicId: epic.id,
        priority: Math.max(10 - index, 1),
      }));
      setPriorities(initialPriorities);
    }
  }, [epics, priorities.length]);

  // Select all suggestions by default when results arrive
  useEffect(() => {
    if (optimizationResult?.allocations) {
      setSelectedSuggestions(new Set(optimizationResult.allocations.map((_, i) => i)));
    }
  }, [optimizationResult]);

  // Clear result on close
  useEffect(() => {
    if (!isOpen) {
      dispatch(clearOptimizationResult());
      setSelectedSuggestions(new Set());
    }
  }, [isOpen, dispatch]);

  const handleConstraintChange = (key: keyof OptimizationConstraints, value: number) => {
    setConstraints((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handlePriorityChange = (epicId: string, priority: number) => {
    setPriorities((prev) => {
      const existing = prev.find((p) => p.epicId === epicId);
      if (existing) {
        return prev.map((p) => (p.epicId === epicId ? { ...p, priority } : p));
      }
      return [...prev, { epicId, priority }];
    });
  };

  const handleRunOptimization = async () => {
    try {
      await dispatch(
        runOptimization({
          scenarioId,
          constraints,
          priorities,
        })
      ).unwrap();
    } catch (error) {
      console.error('Optimization failed:', error);
    }
  };

  const handleToggleSuggestion = (index: number) => {
    setSelectedSuggestions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (optimizationResult?.allocations) {
      setSelectedSuggestions(new Set(optimizationResult.allocations.map((_, i) => i)));
    }
  };

  const handleSelectNone = () => {
    setSelectedSuggestions(new Set());
  };

  const handleApplySuggestions = async () => {
    if (!optimizationResult?.allocations) return;

    const suggestionsToApply = optimizationResult.allocations.filter((_, i) =>
      selectedSuggestions.has(i)
    );

    try {
      await dispatch(
        applyOptimizationSuggestions({
          scenarioId,
          suggestions: suggestionsToApply,
        })
      ).unwrap();

      onOptimizationApplied?.();
      onClose();
    } catch (error) {
      console.error('Failed to apply suggestions:', error);
    }
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Group epics by IS category for better organization
  const epicsByCategory = useMemo(() => {
    const grouped: Record<string, Epic[]> = {
      BUSINESS: [],
      TECH: [],
      SUPPORT: [],
      UNKNOWN: [],
    };

    epics.forEach((epic) => {
      const category = epic.investibleStructure?.category || 'UNKNOWN';
      grouped[category].push(epic);
    });

    return grouped;
  }, [epics]);

  const formatTeamWeeks = (value: number): string => `${value.toFixed(1)} TW`;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-[var(--surface)] border-l border-[var(--border-primary)] shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[var(--border-primary)] flex items-center justify-between bg-[var(--bg-tertiary)]/50">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500/20 to-sky-500/20">
              <Zap className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Optimization Engine</h2>
              <p className="text-sm text-muted-foreground">{scenarioName}</p>
            </div>
          </div>
          <GlassButton variant="ghost" onClick={onClose}>
            <X className="w-5 h-5" />
          </GlassButton>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Constraints Section */}
          <GlassCard className="overflow-hidden">
            <button
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-[var(--bg-hover)]/50 transition-colors"
              onClick={() => toggleSection('constraints')}
            >
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium text-foreground">Optimization Constraints</span>
              </div>
              {expandedSections.constraints ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </button>

            {expandedSections.constraints && (
              <div className="px-4 pb-4 space-y-4">
                {/* Max Team Utilization */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-foreground">Max Team Utilization</label>
                    <span className="text-sm font-medium text-foreground">
                      {Math.round(constraints.maxTeamUtilization * 100)}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min="50"
                    max="100"
                    value={constraints.maxTeamUtilization * 100}
                    onChange={(e) =>
                      handleConstraintChange('maxTeamUtilization', parseInt(e.target.value) / 100)
                    }
                    className="w-full h-2 bg-[var(--bg-hover)]/50 rounded-lg appearance-none cursor-pointer accent-blue-500"
                  />
                </div>

                {/* Category Allocation Targets */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs text-muted-foreground">Business Min</label>
                      <span className="text-xs font-medium text-blue-400">
                        {Math.round(constraints.minBusinessAllocation * 100)}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={constraints.minBusinessAllocation * 100}
                      onChange={(e) =>
                        handleConstraintChange('minBusinessAllocation', parseInt(e.target.value) / 100)
                      }
                      className="w-full h-1.5 bg-[var(--bg-hover)]/50 rounded-lg appearance-none cursor-pointer accent-blue-500"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs text-muted-foreground">Tech Min</label>
                      <span className="text-xs font-medium text-sky-400">
                        {Math.round(constraints.minTechAllocation * 100)}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={constraints.minTechAllocation * 100}
                      onChange={(e) =>
                        handleConstraintChange('minTechAllocation', parseInt(e.target.value) / 100)
                      }
                      className="w-full h-1.5 bg-[var(--bg-hover)]/50 rounded-lg appearance-none cursor-pointer accent-sky-500"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs text-muted-foreground">Support Min</label>
                      <span className="text-xs font-medium text-orange-400">
                        {Math.round(constraints.minSupportAllocation * 100)}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={constraints.minSupportAllocation * 100}
                      onChange={(e) =>
                        handleConstraintChange('minSupportAllocation', parseInt(e.target.value) / 100)
                      }
                      className="w-full h-1.5 bg-[var(--bg-hover)]/50 rounded-lg appearance-none cursor-pointer accent-orange-500"
                    />
                  </div>
                </div>
              </div>
            )}
          </GlassCard>

          {/* Priority Section */}
          <GlassCard className="overflow-hidden">
            <button
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-[var(--bg-hover)]/50 transition-colors"
              onClick={() => toggleSection('priorities')}
            >
              <div className="flex items-center gap-2">
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium text-foreground">EPIC Priorities</span>
                <span className="text-xs text-muted-foreground">({epics.length} EPICs)</span>
              </div>
              {expandedSections.priorities ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </button>

            {expandedSections.priorities && (
              <div className="px-4 pb-4 max-h-60 overflow-y-auto">
                <p className="text-xs text-muted-foreground mb-3">
                  Higher priority EPICs are allocated first (1-10 scale)
                </p>
                <div className="space-y-2">
                  {epics.slice(0, 20).map((epic) => {
                    const currentPriority = priorities.find((p) => p.epicId === epic.id)?.priority || 5;
                    return (
                      <div
                        key={epic.id}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-[var(--bg-hover)]/50"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground truncate">{epic.name}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {epic.investibleStructure?.name} • {formatTeamWeeks(epic.requestedWeeks || 0)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="range"
                            min="1"
                            max="10"
                            value={currentPriority}
                            onChange={(e) =>
                              handlePriorityChange(epic.id, parseInt(e.target.value))
                            }
                            className="w-20 h-1.5 bg-[var(--bg-hover)]/50 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                          />
                          <span className="w-6 text-sm font-medium text-cyan-400 text-right">
                            {currentPriority}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  {epics.length > 20 && (
                    <p className="text-xs text-muted-foreground text-center py-2">
                      +{epics.length - 20} more EPICs
                    </p>
                  )}
                </div>
              </div>
            )}
          </GlassCard>

          {/* Run Button */}
          <GlassButton
            variant="gradient"
            onClick={handleRunOptimization}
            disabled={optimizationLoading}
            className="w-full"
          >
            {optimizationLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Running Optimization...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Run Optimization
              </>
            )}
          </GlassButton>

          {/* Error Display */}
          {optimizationError && (
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-red-400">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm">{optimizationError}</span>
              </div>
            </div>
          )}

          {/* Results Section */}
          {optimizationResult && (
            <GlassCard className="overflow-hidden">
              <button
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-[var(--bg-hover)]/50 transition-colors"
                onClick={() => toggleSection('results')}
              >
                <div className="flex items-center gap-2">
                  {optimizationResult.feasible ? (
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                  )}
                  <span className="font-medium text-foreground">Optimization Results</span>
                  <GlassBadge variant={optimizationResult.feasible ? 'success' : 'warning'}>
                    {optimizationResult.feasible ? 'Feasible' : 'Partial'}
                  </GlassBadge>
                </div>
                {expandedSections.results ? (
                  <ChevronUp className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                )}
              </button>

              {expandedSections.results && (
                <div className="px-4 pb-4 space-y-4">
                  {/* Message */}
                  <p className="text-sm text-muted-foreground">{optimizationResult.message}</p>

                  {/* Metrics */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 bg-[var(--bg-tertiary)]/50 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Total Demand</p>
                      <p className="text-lg font-bold text-foreground">
                        {formatTeamWeeks(optimizationResult.metrics.totalDemand)}
                      </p>
                    </div>
                    <div className="p-3 bg-[var(--bg-tertiary)]/50 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Total Supply</p>
                      <p className="text-lg font-bold text-foreground">
                        {formatTeamWeeks(optimizationResult.metrics.totalSupply)}
                      </p>
                    </div>
                    <div className="p-3 bg-[var(--bg-tertiary)]/50 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Utilization</p>
                      <p className={`text-lg font-bold ${
                        optimizationResult.metrics.utilizationRate > 100
                          ? 'text-red-400'
                          : optimizationResult.metrics.utilizationRate > 90
                          ? 'text-amber-400'
                          : 'text-green-400'
                      }`}>
                        {optimizationResult.metrics.utilizationRate.toFixed(1)}%
                      </p>
                    </div>
                  </div>

                  {/* Warnings */}
                  {optimizationResult.warnings.length > 0 && (
                    <div className="space-y-1">
                      {optimizationResult.warnings.map((warning, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm text-amber-400">
                          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>{warning}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Suggestions */}
                  {optimizationResult.allocations.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-foreground">
                          Suggested Allocations ({optimizationResult.allocations.length})
                        </h4>
                        <div className="flex gap-2">
                          <button
                            onClick={handleSelectAll}
                            className="text-xs text-cyan-400 hover:text-cyan-300"
                          >
                            Select All
                          </button>
                          <span className="text-muted-foreground">|</span>
                          <button
                            onClick={handleSelectNone}
                            className="text-xs text-muted-foreground hover:text-foreground"
                          >
                            Clear
                          </button>
                        </div>
                      </div>

                      <div className="max-h-48 overflow-y-auto space-y-2">
                        {optimizationResult.allocations.map((suggestion, index) => (
                          <label
                            key={index}
                            className="flex items-start gap-3 p-2 rounded-lg hover:bg-[var(--bg-hover)]/50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedSuggestions.has(index)}
                              onChange={() => handleToggleSuggestion(index)}
                              className="mt-1 rounded border-gray-600 text-cyan-500 focus:ring-cyan-500/50"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-foreground truncate">
                                  {suggestion.epicName}
                                </span>
                                <span className="text-xs text-muted-foreground">→</span>
                                <span className="text-sm text-muted-foreground truncate">
                                  {suggestion.teamName}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-cyan-400 font-medium">
                                  {formatTeamWeeks(suggestion.allocatedWeeks)}
                                </span>
                                <span className="text-xs text-muted-foreground">{suggestion.reason}</span>
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Unmet Demand */}
                  {optimizationResult.metrics.unmetDemand.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-amber-400 mb-2">
                        Unmet Demand ({optimizationResult.metrics.unmetDemand.length})
                      </h4>
                      <div className="space-y-1">
                        {optimizationResult.metrics.unmetDemand.slice(0, 5).map((item, i) => (
                          <div key={i} className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground truncate">{item.epicName}</span>
                            <span className="text-red-400">-{formatTeamWeeks(item.gap)}</span>
                          </div>
                        ))}
                        {optimizationResult.metrics.unmetDemand.length > 5 && (
                          <p className="text-xs text-muted-foreground">
                            +{optimizationResult.metrics.unmetDemand.length - 5} more
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </GlassCard>
          )}

          {/* Apply Suggestions Error */}
          {applySuggestionsError && (
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-red-400">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm">{applySuggestionsError}</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {optimizationResult && optimizationResult.allocations.length > 0 && (
          <div className="px-6 py-4 border-t border-[var(--border-primary)] bg-[var(--bg-tertiary)]/50">
            <GlassButton
              variant="primary"
              onClick={handleApplySuggestions}
              disabled={applySuggestionsLoading || selectedSuggestions.size === 0}
              className="w-full"
            >
              {applySuggestionsLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Applying Suggestions...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Apply {selectedSuggestions.size} Selected Suggestions
                </>
              )}
            </GlassButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default OptimizationPanel;
